import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { MoodKey } from '@llura/constants';

import { api, ApiError } from '../lib/api-client';

export type Mood = MoodKey;

export interface JournalEntry {
  id: string;
  body: string;
  /** Null when the user skipped the mood prompt. */
  mood: Mood | null;
  /** Epoch ms of when this entry was written. */
  at: number;
}

export interface JournalState {
  entries: JournalEntry[];

  addEntry: (input: { body: string; mood: Mood | null }) => JournalEntry;
  deleteEntry: (id: string) => void;
  hydrate: (entries: JournalEntry[]) => void;
  reset: () => void;
}

function newId(): string {
  // Avoid `crypto.randomUUID` to stay friendly on older / non-secure contexts.
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export const useJournalStore = create<JournalState>()(
  persist(
    (set) => ({
      entries: [],
      addEntry: ({ body, mood }) => {
        const tempId = newId();
        const entry: JournalEntry = {
          id: tempId,
          body: body.trim(),
          mood,
          at: Date.now(),
        };
        set((s) => ({ entries: [entry, ...s.entries].slice(0, 200) }));
        // Background sync — reconcile the temp id with the server's once it
        // returns. 401 means we're pre-auth; keep the local entry and move on.
        api.journal
          .create({ body: entry.body, mood })
          .then((server) => {
            set((s) => ({
              entries: s.entries.map((e) =>
                e.id === tempId ? { id: server.id, body: server.body, mood: server.mood, at: server.at } : e,
              ),
            }));
          })
          .catch((err: unknown) => {
            if (err instanceof ApiError && err.status === 401) return;
            console.warn('[journal] background sync failed', err);
          });
        return entry;
      },
      deleteEntry: (id) => {
        set((s) => ({ entries: s.entries.filter((e) => e.id !== id) }));
        api.journal.delete(id).catch((err: unknown) => {
          if (err instanceof ApiError && (err.status === 401 || err.status === 404)) return;
          console.warn('[journal] background delete failed', err);
        });
      },
      hydrate: (entries) => set({ entries: entries.slice(0, 200) }),
      reset: () => set({ entries: [] }),
    }),
    {
      name: 'llura.journal',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);
